<template>
  <div class="card" v-show="isSection('edu-history')">
    <div class="overflow-scroll-y">
      <div class="card-header">
        <template v-if="!hideHeading">
          <h2>学歴を入力</h2>
          <p>「自動入力」を使うと、入学・卒業年度を自動で計算できます</p>
          <el-checkbox v-model="formatData.notDisplayIjyo"
            >【以上】を表示しない</el-checkbox
          >
        </template>
        <div v-else class="compact-heading">
          <h3>{{ compactTitle }}</h3>
          <AutoEducationalBackgrounds
            :year="basicData.birthYear"
            :month="basicData.birthMonth"
            :day="basicData.birthDay"
            @create-educational-background="setEducationalBackgrounds"
          />
        </div>
      </div>
      <div class="card-content-list">
        <!--
          統合画面の【以上】を表示しない は職歴カード側に1つだけ置く。
          学歴・職歴は1つの表で「以上」も最終行の1つだけのため、
          両カードに出すと同じ値を共有した重複チェックになる。
        -->
        <div class="add-item">
          <div class="edu-history-action">
            <AutoEducationalBackgrounds
              v-if="!hideHeading"
              :year="basicData.birthYear"
              :month="basicData.birthMonth"
              :day="basicData.birthDay"
              @create-educational-background="setEducationalBackgrounds"
            />
            <div class="button-reset">
              <el-button
                v-if="
                  formatData.educationalHistories &&
                  formatData.educationalHistories.length > 1
                "
                @click="educationalResetVisible = true"
                >リセット</el-button
              >
            </div>
            <NewRirekishoResetDialog
              :value="educationalResetVisible"
              name="educational-reset"
              title="学歴リセット"
              message="入力されている学歴をすべて削除します。よろしいですか？"
              actions-class="text-left"
              @input="educationalResetVisible = $event"
              @confirm="resetEducationalBackgrounds"
            />
          </div>
        </div>
        <div
          class="edit-form-item"
          v-if="
            formatId == 'rirekisho_job_new' ||
            formatId == 'rirekisho_job_new_single'
          "
        >
          <div class="item-name">卒業予定</div>
          <div class="item-body">
            <el-select
              v-model="formatData.gradeYear"
              placeholder="選択してください"
            >
              <el-option
                v-for="item in gradeYearOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>
        </div>
        <div class="edit-form-list">
          <transition-group name="fade" tag="div">
            <HistoryItem
              v-for="(item, index) in formatData.educationalHistories"
              :key="item.uuid"
              :item="item"
              :length="formatData.educationalHistories.length"
              :index="index"
              :id="item.uuid"
              :class="{ active: activeHistoryItem == item.uuid }"
              :minYear="basicData.birthYear"
              :createDate="formatData.createDate"
              :descriptionPlaceholder="'学歴を入力'"
              :descriptionMaxLength="descriptionMaxLength"
              @add-history-item="addEducationalHistory"
              @delete-history-item="deleteEducationalHistory"
              @move-history-item="moveEducationalHistory"
              @click.native="activateHistoryItem(item.uuid)"
            />
          </transition-group>
          <div class="add-list-item">
            <el-button
              icon="el-icon-plus"
              circle
              type="success"
              @click="addEducationalHistory()"
            />
          </div>
        </div>
        <div class="attention-limit column" v-if="previewErrors.eduJobHistory">
          <i class="el-icon-warning"></i> {{ previewErrors.eduJobHistory }}
        </div>
      </div>
      <div v-if="formatId == 'rirekisho_job_change_old'">
        <p class="guidetxt">学歴の見本（参考にして作成しましょう）</p>
        <img
          src="/static/img/rirekishoguide/guide03.png"
          alt="学歴"
          class="guideimg"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import HistoryItem from '@/components/formats/RirekishoParts/HistoryItem.vue'
import AutoEducationalBackgrounds from '@/components/formats/RirekishoParts/AutoEducationalBackgrounds.vue'
import NewRirekishoResetDialog from '@/components/format-edit/new-rirekisho/NewRirekishoResetDialog.vue'
import gradeYearOptions from '@/utils/format-edit/gradeYearOptions.json'
import { useRirekishoFormatData } from '@/composables/format-edit/useRirekishoFormatData'
import { useRirekishoHistoriesApi } from '@/composables/format-edit/useRirekishoHistories'
import type { BasicData } from '@/utils/format-edit/basicData'
import { isCardSectionActive } from '@/utils/format-edit/cardSections'

const descriptionMaxLength = 80

const props = withDefaults(
  defineProps<{
    currentCardId?: string
    formatId?: string
    basicData: BasicData
    previewErrors: Record<string, string>
    /** 統合画面では見出し・リード文を出さず、短い見出しだけ帯に置く */
    hideHeading?: boolean
    compactTitle?: string
  }>(),
  {
    hideHeading: false,
    compactTitle: ''
  }
)

// 書式によって学歴・職歴は 1 画面へ統合される（カード定義の `sections`）
const isSection = (section: string) =>
  isCardSectionActive(props.formatId, props.currentCardId, section)

const formatData = useRirekishoFormatData()
const {
  activeHistoryItem,
  setEducationalBackgrounds,
  resetEducationalBackgrounds,
  addEducationalHistory,
  deleteEducationalHistory,
  moveEducationalHistory,
  activateHistoryItem
} = useRirekishoHistoriesApi()

const educationalResetVisible = ref(false)
</script>

<style lang="scss" scoped>
@import '@/assets/scss/format-edit/rirekisho-guide';

// 統合画面: 見出しと「自動入力」をヘッダ帯の中に横並びで置く
.compact-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  // 自動入力ダイアログの入れ物（高さ 0）が margin-top を持つため、
  // ヘッダ帯の高さが職歴側とズレる。打ち消す。
  :deep(.education-option) {
    margin-top: 0;
  }
}
</style>
